import { useEffect, useRef } from "react";
import { useLocation } from "wouter";
import { isRouteScrollResetHeld } from "@/lib/guidedTourScroll";
import { pageReturnPending } from "@/lib/pageReturn";

/**
 * b18k: centralised scroll restoration on tab change.
 *
 * Resets every plausible scroll owner (window, document.scrollingElement,
 * documentElement, body, and any [data-scroll-root] containers) on each
 * route change. The reset fires immediately on commit AND again after a
 * double requestAnimationFrame so a late WebView scroll-restore (Capacitor
 * iOS) cannot re-apply the previous offset after the new page paints.
 *
 * Optional second argument: an array of path prefixes that should NOT trigger
 * a reset (e.g. sub-routes that are internal-state navigation, not true tab
 * switches). Both the incoming and outgoing location must share the prefix for
 * the reset to be suppressed.
 *
 * Usage:
 *   useScrollResetOnRoute();
 *   // or, to skip resets for sub-paths:
 *   useScrollResetOnRoute({ skipPrefixes: ["/dex/detail/"] });
 *
 * Wire this hook ONCE at the routing root (AppShell or App). Do not call it
 * from individual page components -- that is the duplication this hook was
 * created to eliminate.
 */

interface UseScrollResetOnRouteOptions {
  /**
   * Path prefixes for which a route change should NOT trigger a scroll reset.
   * Useful when sub-navigation within a tab changes the URL but should
   * preserve scroll (e.g. opening a detail sheet that pushes a sub-path).
   * The check is: if the current location AND the previous location both start
   * with one of these prefixes the reset is skipped.
   */
  skipPrefixes?: string[];
  /**
   * Path prefixes such that RETURNING from one of them INTO a skipPrefixes
   * region also suppresses the reset — even though the outgoing route is not
   * itself in the skip region. This is the Pokémon-detail → Dex return: the
   * Dex owns its own scroll restore (dexScrollRestore), and the central reset
   * must not fire and scroll it back to the top. Entering a detail page (INTO
   * one of these prefixes) still resets, so a detail page always opens at the
   * top.
   */
  preserveReturnFromPrefixes?: string[];
  /**
   * Exact `to` destinations whose reset is suppressed only when arriving from a
   * route under `from`. This is the pair form of the two prefix options above,
   * for the case where the detail route is NESTED under the page that owns the
   * restore: /my-pokemon/:id → /my-pokemon must not reset (the list restores the
   * row the trainer was on), while /my-pokemon → /my-pokemon/:id and a swipe
   * from one record to the next still open at the top. A prefix pair cannot
   * express that, because every one of those routes shares the prefix.
   */
  returnPairs?: Array<{ to: string; from: string }>;
}

function resetAllScrollOwners(): void {
  try {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" as ScrollBehavior });
  } catch {
    window.scrollTo(0, 0);
  }
  const candidates: Array<Element | null> = [
    document.scrollingElement,
    document.documentElement,
    document.body,
  ];
  for (const el of candidates) {
    if (el) {
      // element.scrollTo FIRST: per spec a programmatic scroll aborts an
      // in-flight smooth scroll, and WKWebView honours the abort for
      // scrollTo() more reliably than for a bare scrollTop write. Without
      // this, a Calendar day-strip smooth scrollIntoView still animating at
      // navigation time carried its offset onto the NEXT page (device
      // report 2026-08-21). The scrollTop writes stay as the belt for
      // environments where scrollTo is missing (jsdom) or a no-op.
      try {
        (el as HTMLElement).scrollTo?.({ top: 0, left: 0, behavior: "instant" as ScrollBehavior });
      } catch {
        /* jsdom / ancient WebView */
      }
      el.scrollTop = 0;
      el.scrollLeft = 0;
    }
  }
  document.querySelectorAll<HTMLElement>("[data-scroll-root]").forEach((el) => {
    try {
      el.scrollTo?.({ top: 0, left: 0, behavior: "instant" as ScrollBehavior });
    } catch {
      /* jsdom */
    }
    el.scrollTop = 0;
    el.scrollLeft = 0;
  });
}

/**
 * True when any scroll owner is currently scrolled away from the top-left.
 *
 * Used to decide whether the POST-PAINT reset is actually needed. On iOS the
 * WKWebView (Capacitor) can leave the compositor showing stale/blank tiles when
 * a programmatic scroll fires AFTER the new page has painted — so on a normal
 * tab switch, where the synchronous pre-paint reset already put the page at the
 * top, the post-paint scrollTo is a redundant no-op that nonetheless triggers
 * the blank-until-you-scroll bug. We only re-run it when the WebView has
 * genuinely restored a non-zero offset (the case the post-paint pass exists for).
 */
function isScrolledAway(): boolean {
  if (typeof window !== "undefined" && (window.scrollY || window.scrollX)) return true;
  const owners: Array<Element | null> = [
    document.scrollingElement,
    document.documentElement,
    document.body,
  ];
  for (const el of owners) {
    if (el && (el.scrollTop || el.scrollLeft)) return true;
  }
  for (const el of Array.from(document.querySelectorAll<HTMLElement>("[data-scroll-root]"))) {
    if (el.scrollTop || el.scrollLeft) return true;
  }
  return false;
}

export function useScrollResetOnRoute(options?: UseScrollResetOnRouteOptions): void {
  const {
    skipPrefixes = [],
    preserveReturnFromPrefixes = [],
    returnPairs = [],
  } = options ?? {};
  const [location] = useLocation();
  // Track the previous location so we can suppress the reset ONLY for
  // sub-navigation that stays within the same skip region (e.g. swiping
  // between dex variant surfaces). Entering the skip region from a different
  // tab still resets — that is the documented PR #242 behaviour and prevents
  // a dex page from inheriting the previous tab's scroll offset.
  const prevLocationRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    const prevLocation = prevLocationRef.current;
    prevLocationRef.current = location;

    // A Back navigation with an offset to restore (pageReturn, 2026-10-12):
    // the arriving page puts itself back where it was (usePageReturnRestore),
    // so the reset and every late guard below stand down for this arrival.
    if (prevLocation !== undefined && pageReturnPending(location)) {
      return;
    }

    // Suppress the reset when the incoming location is in the skip region AND
    // the outgoing location is EITHER in the skip region (in-region sub-nav,
    // e.g. dex variant swipes) OR in a preserve-return region (the Pokémon
    // detail → Dex back button, where the Dex restores its own scroll). A true
    // tab change into the region from anywhere else still resets.
    if (
      skipPrefixes.length > 0 &&
      prevLocation !== undefined &&
      skipPrefixes.some((prefix) => location.startsWith(prefix)) &&
      (skipPrefixes.some((prefix) => prevLocation.startsWith(prefix)) ||
        preserveReturnFromPrefixes.some((prefix) => prevLocation.startsWith(prefix)))
    ) {
      return;
    }

    // Suppress the reset for an exact return pair (a nested detail route
    // returning to the page that owns its scroll restore).
    if (
      prevLocation !== undefined &&
      returnPairs.some((p) => location === p.to && prevLocation.startsWith(p.from))
    ) {
      return;
    }

    // 1) Synchronous reset on commit so the new page starts at the top.
    resetAllScrollOwners();

    // 2) Post-paint reset overrides any WebView scroll-restore that fires after
    //    the layout pass — but ONLY when something actually restored a non-zero
    //    offset. A post-paint scrollTo(0,0) on a page that is already at the top
    //    is what caused the iOS/Capacitor "blank until you scroll" bug: WKWebView
    //    leaves the new page's tiles uncomposited after a redundant programmatic
    //    scroll. Guarding on isScrolledAway() keeps the scroll-restore override
    //    for the case it exists for, while removing the no-op scroll that blanked
    //    a freshly-switched tab.
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        // While the guided tour is open it scrolls its own target into view
        // AFTER this route change; re-snapping to the top here would drag the
        // page (and so the tour card) off the control the step describes. The
        // tour owns scroll position while it runs, so stand down.
        if (isScrolledAway() && !isRouteScrollResetHeld()) resetAllScrollOwners();
      });
    });

    // 3) Late guarded passes for a SMOOTH scroll still animating at navigation
    //    time (the Calendar day-strip glide): on WKWebView the animation can
    //    outlive both rAF passes and land its offset on the new page. Guarded
    //    by isScrolledAway(), so on an already-settled page these are pure
    //    no-ops and cannot re-trigger the blank-tiles bug the rAF guard
    //    exists for.
    const lateTimers = [280, 800].map((ms) =>
      window.setTimeout(() => {
        if (isScrolledAway() && !isRouteScrollResetHeld()) resetAllScrollOwners();
      }, ms),
    );

    // 4) Event-driven guard for restores the fixed timers can't bound: the iOS
    //    WKWebView re-applies the previous page's offset natively once the new
    //    page's layout grows tall enough — on a heavy page (the 1,025-cell Dex
    //    grid) that can land well after the 800ms pass, leaving the new page
    //    parked mid-scroll with content-visibility cells unpainted. Any such
    //    restore fires scroll events, so for a short window we listen (capture
    //    reaches child scrollers too) and re-zero on any non-user scroll. The
    //    first genuine user input (touch/wheel/key/pointer) disarms the guard
    //    immediately so it can never fight a real scroll the trainer starts.
    const GUARD_MS = 2000;
    let guardArmed = true;
    const disarmEvents = ["touchstart", "wheel", "keydown", "pointerdown"] as const;
    const disarm = () => {
      guardArmed = false;
    };
    const onGuardScroll = () => {
      if (!guardArmed) return;
      if (isScrolledAway() && !isRouteScrollResetHeld()) resetAllScrollOwners();
    };
    document.addEventListener("scroll", onGuardScroll, { capture: true, passive: true });
    for (const type of disarmEvents) {
      window.addEventListener(type, disarm, { capture: true, passive: true });
    }
    const guardTimer = window.setTimeout(disarm, GUARD_MS);

    return () => {
      cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
      for (const t of lateTimers) window.clearTimeout(t);
      document.removeEventListener("scroll", onGuardScroll, { capture: true });
      for (const type of disarmEvents) {
        window.removeEventListener(type, disarm, { capture: true });
      }
      window.clearTimeout(guardTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);
}
